import { DestroyRef, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { Subject, Subscription } from 'rxjs';
import {
  AssessmentItemPosition,
  AssessmentUserResponseStatus,
  LearnosityItemType,
} from '../../models/assessment-models';
import { AssessmentPlayerService } from '../../services/player.service';
import { noNav } from '../../store/actions/page-content.actions';
import { PageContentState } from '../../store/models/assessment-models';
import { TestletActionButtonType } from '../common/testlet-action-buttons/models/testlet-action-buttons.models';

export abstract class BaseTestletHandler {
  /**
   * @description Read only property that keep list of different 'Item Types' tags.
   */
  protected readonly itemTypes = LearnosityItemType;

  /**
   * @description Property to track if the last question of the Assessment is loaded.
   */
  protected isLastQuestionLoaded = false;

  /**
   * @deescription Property to track current question index.
   */
  protected currentQuestionIndex = 0;

  /**
   * @description Property to keep track of all the open subscriptions.
   */
  private notifierSubscriptions: Subscription[] = [];

  /**
   * @description Property to set methods to execute for different Assessment Player Events.
   * 1. When Assessment question is loaded in the DOM.
   * 2. When Assessment question is changed in the DOM.
   * 3. When Assessment questions are all attempted and finish assessment is triggered.
   * 4. When Assessment is completed and is removed from DOM.
   */
  protected eventHandlers: {
    questionLoadEventNotifier$?: (itemPosition: AssessmentItemPosition) => void;
    questionChangeEventNotifier$?: (response: AssessmentUserResponseStatus) => void;
    testSubmitBeginNotifier$?: () => void;
    testUnloadEventNotifier$?: () => void;
  };

  protected destroyRef = inject(DestroyRef);
  protected buttonClickSubject = new Subject<TestletActionButtonType>();

  protected constructor(
    private service: AssessmentPlayerService,
    private pageContentStore: Store<PageContentState>,
  ) {}

  /**
   * @description Initialize subscriptions required for the Assessment Player.
   */
  protected initializeListeners(initObject: string, currentTestlet: string, sessionId: string, assessmentId: number) {
    this.service.initializeTestlet(initObject, currentTestlet, sessionId, assessmentId);

    this.resetQuestionPageControls();

    this.initializeNotifications();
  }

  private initializeNotifications() {
    this.resetNotifications();

    const loadSubscription = this.service.questionLoadEventNotifier$
      .asObservable()
      .subscribe(this.eventHandlers.questionLoadEventNotifier$);
    this.notifierSubscriptions.push(loadSubscription);

    const questionChangeSubscription = this.service.questionChangeEventNotifier$
      .asObservable()
      .subscribe(this.eventHandlers.questionChangeEventNotifier$);
    this.notifierSubscriptions.push(questionChangeSubscription);
  }

  private resetNotifications() {
    this.notifierSubscriptions.forEach((s) => {
      if (!!s && !s.closed) {
        s.unsubscribe();
      }
    });

    this.notifierSubscriptions = [];
  }

  /**
   * @description Method to be called upon component 'ngOnDestroy()'.
   * 1. Destroys the loaded Learnosity Widget.
   * 2. Reset 'Assessment Page Footer' actions.
   * 3. Close all the open subscriptions.
   */
  protected onDestroy() {
    this.service.destroyItemsApp();
    this.pageContentStore.dispatch(noNav());
    this.resetNotifications();
  }

  /**
   * 1. Set the property for current question index.
   * 2. Set the flag for whether last question is loaded or not.
   *
   * @param itemPosition Current Assessment Position Flag.
   */
  protected setQuestionPageControls(itemPosition: AssessmentItemPosition) {
    this.currentQuestionIndex = itemPosition.currentItemIndex;
    this.isLastQuestionLoaded = itemPosition.isLastItem;
    this.pageContentStore.dispatch(noNav());
  }

  /**
   * @description Method to render next Assessment Item or initiate finish Assessment based on whether if last question loaded.
   */
  protected navigateToNextItem() {
    if (this.isLastQuestionLoaded) {
      this.service.finishAssessment();
    } else {
      this.service.goToNextItem();
    }
  }

  public moveNext() {
    if (this.service.isLastInstruction()) {
      this.pageContentStore.dispatch(noNav());
    }
    this.navigateToNextItem();
  }

  protected handleButtonClicked(type: TestletActionButtonType): void {
    this.buttonClickSubject.next(type);
  }

  protected handleDebouncedButtonClick(type: TestletActionButtonType): void {
    if (type === 'next' || type === 'start') {
      this.moveNext();
    } else if (type === 'repeat') {
      this.service.repeatInstructions();
    }
  }

  protected handleDebouncedClick(event: Event): void {
    const targetElement = event.target as HTMLElement;
    const tagName = targetElement.tagName;
    if (tagName !== 'INPUT') {
      return;
    }

    if (this.service.isLastInstruction()) {
      this.pageContentStore.dispatch(noNav());
    }

    this.navigateToNextItem();
  }

  private resetQuestionPageControls() {
    this.currentQuestionIndex = 0;
    this.isLastQuestionLoaded = false;
  }
}
